// src/pages/DashboardPage.js - Reports Dashboard (Sandvik theme)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Globe, MapPin, BarChart3, TrendingUp, TrendingDown,
  Clock, Star, StarOff, BellOff, Eye, Search, ChevronRight,
  RefreshCw, X, Check, CheckCheck, AlertCircle,
  ArrowUpRight, Hash, Layers, ExternalLink
} from 'lucide-react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

// Animated counter
const AnimatedNumber = ({ value, duration = 1200 }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef();

  useEffect(() => {
    const target = Number(value) || 0;
    const start = display;
    const diff = target - start;
    if (diff === 0) return;
    const startTime = performance.now();

    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(start + diff * eased));
      if (progress < 1) ref.current = requestAnimationFrame(tick);
    };
    ref.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(ref.current);
  }, [value, duration]);

  return <span>{display.toLocaleString('sv-SE')}</span>;
};

// Skeleton loader
const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 ${className}`} />
);

const KpiSkeleton = () => (
  <div className="bg-white shadow p-5 border border-gray-100">
    <div className="flex items-center space-x-4">
      <Skeleton className="h-10 w-10 flex-shrink-0" />
      <div className="flex-1">
        <Skeleton className="h-3 w-20 mb-2" />
        <Skeleton className="h-7 w-16 mb-1" />
        <Skeleton className="h-3 w-24" />
      </div>
    </div>
  </div>
);

// KPI Card
const KpiCard = ({ icon: Icon, label, value, subtitle, trend, onClick }) => (
  <div
    className={'bg-white shadow hover:shadow-lg transition-all duration-300 border border-gray-100 group' + (onClick ? ' cursor-pointer' : '')}
    onClick={onClick}
  >
    <div className="p-5 flex items-start space-x-4">
      <div className="w-10 h-10 bg-black flex items-center justify-center flex-shrink-0">
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-medium text-gray-500 tracking-wider">{label}</p>
          {trend !== undefined && trend !== null && (
            <div className={`flex items-center space-x-1 text-[11px] font-semibold px-1.5 py-0.5 ${trend > 0 ? 'text-emerald-700 bg-emerald-50' : trend < 0 ? 'text-red-600 bg-red-50' : 'text-gray-500 bg-gray-50'}`}>
              {trend > 0 ? <TrendingUp className="h-3 w-3" /> : trend < 0 ? <TrendingDown className="h-3 w-3" /> : null}
              <span>{trend > 0 ? '+' : ''}{Number(trend).toLocaleString('sv-SE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%</span>
            </div>
          )}
        </div>
        <p className="text-2xl font-bold text-gray-900 leading-tight mt-0.5">
          <AnimatedNumber value={value} />
        </p>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  </div>
);

// Report type badge
const ReportTypeBadge = ({ type }) => {
  const colors = {
    RTC: 'bg-black text-white',
    CSV: 'bg-black text-white',
    SA: 'bg-black text-white',
    RA: 'bg-black text-white',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${colors[type] || 'bg-gray-300 text-gray-700'}`}>
      {type}
    </span>
  );
};

// Empty state
const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="w-14 h-14 bg-gray-100 flex items-center justify-center mb-4">
      <Icon className="h-7 w-7 text-gray-400" />
    </div>
    <p className="text-sm font-medium text-gray-700 mb-1">{title}</p>
    <p className="text-xs text-gray-400">{description}</p>
  </div>
);

// PDF Viewer Modal
const PdfViewerModal = ({ reportId, onClose }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const blobUrl = await apiService.getPdfPreviewUrl(reportId);
        if (!cancelled) setUrl(blobUrl);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [reportId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white w-[90vw] h-[90vh] shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <FileText className="h-5 w-5 text-gray-700" />
            <span className="font-semibold text-gray-800">Report PDF - {reportId}</span>
          </div>
          <div className="flex items-center space-x-2">
            {url && (
              <a href={url} target="_blank" rel="noopener noreferrer"
                className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-black border border-gray-300 hover:border-gray-400 transition-colors flex items-center space-x-1">
                <ExternalLink className="h-3.5 w-3.5" /><span>Open in tab</span>
              </a>
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-gray-200 transition-colors">
              <X className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>
        <div className="flex-1 relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
              <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50">
              <AlertCircle className="h-10 w-10 text-red-400 mb-3" />
              <p className="text-sm text-gray-600">{error}</p>
            </div>
          )}
          {url && !loading && (
            <object data={url} type="application/pdf" className="w-full h-full">
              <embed src={url} type="application/pdf" className="w-full h-full" />
            </object>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Dashboard
const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();

  const [kpis, setKpis] = useState(null);
  const [favorites, setFavorites] = useState({ data: [], total_count: 0 });
  const [newRecords, setNewRecords] = useState({ data: [], total_count: 0 });
  const [notifications, setNotifications] = useState([]);
  const [notificationCounts, setNotificationCounts] = useState({ unread_count: 0, total_count: 0 });

  const [kpisLoading, setKpisLoading] = useState(true);
  const [favoritesLoading, setFavoritesLoading] = useState(true);
  const [newRecordsLoading, setNewRecordsLoading] = useState(true);
  const [notificationsLoading, setNotificationsLoading] = useState(true);

  const [pdfReportId, setPdfReportId] = useState(null);
  const [notifFilter, setNotifFilter] = useState('all');
  const [favPage, setFavPage] = useState(1);
  const [newRecPage, setNewRecPage] = useState(1);

  const fetchKpis = useCallback(async () => {
    try {
      setKpisLoading(true);
      const data = await apiService.getKpis();
      setKpis(data);
    } catch { setKpis(null); } finally { setKpisLoading(false); }
  }, []);

  const fetchFavorites = useCallback(async (page = 1) => {
    try {
      setFavoritesLoading(true);
      const data = await apiService.getFavorites(page, 5);
      setFavorites({ data: data.favorites || [], total_count: data.total_count || 0 });
      setFavPage(page);
    } catch { setFavorites({ data: [], total_count: 0 }); } finally { setFavoritesLoading(false); }
  }, []);

  const fetchNewRecords = useCallback(async (page = 1) => {
    try {
      setNewRecordsLoading(true);
      const data = await apiService.getNewRecords(page, 5);
      const records = Array.isArray(data)
        ? data
        : Array.isArray(data?.new_records)
          ? data.new_records
          : Array.isArray(data?.data)
            ? data.data
            : [];

      const totalCount = typeof data?.total_count === 'number' ? data.total_count : records.length;
      setNewRecords({ data: records, total_count: totalCount, since: data?.since || null });
      setNewRecPage(page);
    } catch { setNewRecords({ data: [], total_count: 0 }); } finally { setNewRecordsLoading(false); }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      setNotificationsLoading(true);
      const data = await apiService.getNotifications(notifFilter === 'unread');

      const notificationList = Array.isArray(data)
        ? data
        : Array.isArray(data?.notifications)
          ? data.notifications
          : Array.isArray(data?.data)
            ? data.data
            : [];

      const unreadCount = typeof data?.unread_count === 'number'
        ? data.unread_count
        : notificationList.filter((n) => !n.is_read).length;

      const totalCount = typeof data?.total_count === 'number'
        ? data.total_count
        : notificationList.length;

      setNotifications(notificationList);
      setNotificationCounts({ unread_count: unreadCount, total_count: totalCount });
    } catch {
      setNotifications([]);
      setNotificationCounts({ unread_count: 0, total_count: 0 });
    } finally { setNotificationsLoading(false); }
  }, [notifFilter]);

  useEffect(() => { fetchKpis(); fetchFavorites(); fetchNewRecords(); fetchNotifications(); }, []);
  useEffect(() => { fetchNotifications(); }, [notifFilter]);

  const handleRemoveFavorite = async (reportId) => {
    try {
      await apiService.removeFavorite(reportId);
      showToast('Removed from favorites', 'success');
      fetchFavorites(favPage);
    } catch { showToast('Failed to remove favorite', 'error'); }
  };

  const handleMarkRead = async (ids) => {
    try { await apiService.markNotificationsRead(ids); fetchNotifications(); }
    catch { showToast('Failed to update notification', 'error'); }
  };

  const handleMarkAllRead = async () => {
    try { await apiService.markAllNotificationsRead(); fetchNotifications(); showToast('All notifications marked as read', 'success'); }
    catch { showToast('Failed to mark all read', 'error'); }
  };

  const handleDismissNotification = async (id) => {
    try { await apiService.dismissNotification(id); fetchNotifications(); }
    catch { showToast('Failed to dismiss notification', 'error'); }
  };

  const unreadCount = typeof notificationCounts.unread_count === 'number'
    ? notificationCounts.unread_count
    : notifications.filter(n => !n.is_read).length;

  const kpiCards = kpis ? [
    {
      icon: FileText,
      label: 'Total records',
      value: kpis.total_docs ?? kpis.total_reports ?? 0,
      subtitle: 'All reports',
      onClick: function() { navigate('/dashboard/reports'); }
    },
    {
      icon: Globe,
      label: 'Countries',
      value: kpis.country_count ?? kpis.unique_countries ?? 0,
      subtitle: 'View country drill-down',
      onClick: function() { navigate('/dashboard/drill-down?dimension=countries'); }
    },
    {
      icon: Layers,
      label: 'RTC',
      value: kpis.rtc_count || 0,
      subtitle: 'RTC tab',
      onClick: function() { navigate('/dashboard/reports?tab=RTC'); }
    },
    {
      icon: BarChart3,
      label: 'CSV',
      value: kpis.csv_count || 0,
      subtitle: 'CSV tab',
      onClick: function() { navigate('/dashboard/reports?tab=CSV'); }
    },
    {
      icon: Hash,
      label: 'SA',
      value: kpis.sa_count || 0,
      subtitle: 'SA tab',
      onClick: function() { navigate('/dashboard/reports?tab=SA'); }
    },
    {
      icon: MapPin,
      label: 'RA',
      value: kpis.ra_count || 0,
      subtitle: 'RA tab',
      onClick: function() { navigate('/dashboard/reports?tab=RA'); }
    },
  ] : [];

  const formatDateTime = (d) => {
    if (!d) return '-';
    const date = new Date(d);
    return date.toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const timeAgo = (d) => {
    if (!d) return '';
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `för ${mins} min sedan`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `för ${hrs} tim sedan`;
    const days = Math.floor(hrs / 24);
    return `för ${days} d sedan`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* Header */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1 text-left">Reports dashboard</h1>
            <p className="text-gray-500 text-sm">
              Overview &amp; quick access to your rock tools data
              {kpis && kpis.last_sync_time && (
                <span className="ml-3 inline-flex items-center space-x-1 text-gray-400">
                  <Clock className="h-3.5 w-3.5" />
                  <span>Synced {formatDateTime(kpis.last_sync_time)}</span>
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={() => navigate('/dashboard/reports')}
              className="flex items-center space-x-2 px-5 py-2.5 bg-black text-white text-sm font-medium hover:bg-gray-800 transition-colors shadow">
              <Search className="h-4 w-4" />
              <span>Search reports</span>
            </button>
            <button onClick={() => { fetchKpis(); fetchFavorites(); fetchNewRecords(); fetchNotifications(); }}
              className="p-2.5 border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors" title="Refresh">
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <section className="mb-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {kpisLoading
              ? Array.from({ length: 6 }).map(function(_, i) { return <KpiSkeleton key={i} />; })
              : kpiCards.map(function(kpi, i) { return <KpiCard key={i} {...kpi} />; })
            }
          </div>
        </section>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* LEFT COLUMN */}
          <div className="lg:col-span-3 space-y-6">

            {/* New Records */}
            <section className="bg-white shadow border border-gray-100 flex flex-col h-[420px] text-left text-black">
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <h2 className="text-lg font-bold text-gray-900">Today's records</h2>
                    <p className="text-xs text-black mt-0.5">Reports added today</p>
                  </div>
                  {newRecords.total_count > 0 && (
                    <span className="text-xs font-semibold text-black bg-gray-100 px-2.5 py-1">
                      {newRecords.total_count} today
                    </span>
                  )}
                </div>
              </div>
              <div className="divide-y divide-gray-50 max-h-[520px] overflow-y-auto">
                {newRecordsLoading ? (
                  Array.from({ length: 3 }).map(function(_, i) {
                    return (
                      <div key={i} className="px-6 py-4 flex items-center space-x-4">
                        <Skeleton className="h-8 w-8" />
                        <div className="flex-1">
                          <Skeleton className="h-4 w-3/4 mb-2" />
                          <Skeleton className="h-3 w-1/2" />
                        </div>
                      </div>
                    );
                  })
                ) : newRecords.data && newRecords.data.length > 0 ? (
                  newRecords.data.map(function(rec, idx) {
                    return (
                      <div key={rec.report_id || idx}
                        className="px-6 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors group cursor-pointer"
                        onClick={function() { navigate('/document/'+rec.report_id); }}>
                        <div className="flex items-center space-x-4 min-w-0">
                          <ReportTypeBadge type={rec.report_type} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate group-hover:text-black">
                              {rec.customer_name || rec.site_name || 'Report '+rec.report_id}
                            </p>
                            <p className="text-xs text-black truncate">
                              {[rec.site_name && rec.customer_name ? rec.site_name : '', rec.country_name, rec.date_of_visit]
                                .filter(Boolean)
                                .join(' \u00b7 ')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          <button onClick={function(e) { e.stopPropagation(); setPdfReportId(rec.report_id); }}
                            className="p-1.5 text-gray-400 hover:text-black hover:bg-gray-100 transition-colors" title="View PDF">
                            <Eye className="h-4 w-4" />
                          </button>
                          <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500" />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <EmptyState icon={FileText} title="No records today" description="No reports added today" />
                )}
              </div>
              {newRecords.total_count > 5 && (
                <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-black">Page {newRecPage}</span>
                  <div className="flex space-x-2">
                    <button disabled={newRecPage <= 1} onClick={function() { fetchNewRecords(newRecPage - 1); }}
                      className="px-3 py-1 text-xs border border-gray-300 text-black hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      Prev
                    </button>
                    <button disabled={!newRecords.data || newRecords.data.length < 5} onClick={function() { fetchNewRecords(newRecPage + 1); }}
                      className="px-3 py-1 text-xs border border-gray-300 text-black hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      Next
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* Favorites */}
            <section className="bg-white shadow border border-gray-100 text-left">
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <h2 className="text-lg font-bold text-gray-900">Bookmarked reports</h2>
                    <p className="text-xs text-gray-400 mt-0.5">Your saved favorites for quick access</p>
                  </div>
                  {favorites.total_count > 0 && (
                    <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2.5 py-1">
                      {favorites.total_count} saved
                    </span>
                  )}
                </div>
              </div>
              <div className="divide-y divide-gray-50 text-left">
                {favoritesLoading ? (
                  Array.from({ length: 3 }).map(function(_, i) {
                    return (
                      <div key={i} className="px-6 py-4 flex items-center space-x-4">
                        <Skeleton className="h-8 w-8" />
                        <div className="flex-1">
                          <Skeleton className="h-4 w-3/4 mb-2" />
                          <Skeleton className="h-3 w-1/2" />
                        </div>
                      </div>
                    );
                  })
                ) : favorites.data && favorites.data.length > 0 ? (
                  favorites.data.map(function(fav, idx) {
                    return (
                      <div key={fav.report_id || idx}
                        className="px-6 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors group cursor-pointer"
                        onClick={function() { navigate('/document/'+fav.report_id); }}>
                        <div className="flex items-center space-x-4 min-w-0 text-left">
                          <ReportTypeBadge type={fav.report_type} />
                          <div className="min-w-0 text-left">
                            <p className="text-sm font-medium text-gray-900 truncate group-hover:text-black">
                              {fav.customer_name || fav.site_name || 'Report '+fav.report_id}
                            </p>
                            <p className="text-xs text-gray-400 truncate">
                              {[fav.site_name && fav.customer_name ? fav.site_name : '', fav.country_name, fav.date_of_visit]
                                .filter(Boolean)
                                .join(' \u00b7 ')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          <button onClick={function(e) { e.stopPropagation(); setPdfReportId(fav.report_id); }}
                            className="p-1.5 text-gray-400 hover:text-black hover:bg-gray-100 transition-colors" title="View PDF">
                            <Eye className="h-4 w-4" />
                          </button>
                          <button onClick={function(e) { e.stopPropagation(); handleRemoveFavorite(fav.report_id); }}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="Remove from favorites">
                            <StarOff className="h-4 w-4" />
                          </button>
                          <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500" />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <EmptyState icon={Star} title="No favorites yet" description="Bookmark reports from the search page to see them here" />
                )}
              </div>
              {favorites.total_count > 5 && (
                <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-gray-400">Page {favPage}</span>
                  <div className="flex space-x-2">
                    <button disabled={favPage <= 1} onClick={function() { fetchFavorites(favPage - 1); }}
                      className="px-3 py-1 text-xs border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      Prev
                    </button>
                    <button disabled={!favorites.data || favorites.data.length < 5} onClick={function() { fetchFavorites(favPage + 1); }}
                      className="px-3 py-1 text-xs border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      Next
                    </button>
                  </div>
                </div>
              )}
            </section>
          </div>

          {/* RIGHT COLUMN - Notifications */}
          <div className="lg:col-span-2 space-y-6 text-left">
            <section className="bg-white shadow border border-gray-100 flex flex-col h-[420px] text-left">
              <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-bold text-gray-900">Notifications</h2>
                  {unreadCount > 0 && (
                    <button onClick={handleMarkAllRead}
                      className="text-xs font-medium text-gray-500 hover:text-black flex items-center space-x-1 transition-colors">
                      <CheckCheck className="h-3.5 w-3.5" /><span>Mark all read</span>
                    </button>
                  )}
                </div>
                <div className="flex space-x-1 bg-gray-100 p-0.5">
                  {['all', 'unread'].map(function(f) {
                    return (
                      <button key={f} onClick={function() { setNotifFilter(f); }}
                        className={'flex-1 py-1.5 text-xs font-medium transition-colors '+(notifFilter === f ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700')}>
                        {f === 'all' ? 'All' : 'Unread'+(unreadCount ? ' ('+unreadCount+')' : '')}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="divide-y divide-gray-50 max-h-[520px] overflow-y-auto">
                {notificationsLoading ? (
                  Array.from({ length: 4 }).map(function(_, i) {
                    return (
                      <div key={i} className="px-5 py-4">
                        <Skeleton className="h-4 w-3/4 mb-2" />
                        <Skeleton className="h-3 w-full mb-1" />
                        <Skeleton className="h-3 w-1/3" />
                      </div>
                    );
                  })
                ) : notifications.length > 0 ? (
                  notifications.map(function(notif) {
                    return (
                      <div key={notif.id || notif.notification_id}
                        className={'px-5 py-3.5 transition-colors '+(notif.is_read ? 'bg-white' : 'bg-amber-50/40')}>
                        <div className="flex items-start justify-between">
                          <div className="min-w-0 flex-1 mr-3">
                            <div className="flex items-center space-x-2 mb-1">
                              {!notif.is_read && <span className="w-2 h-2 bg-amber-500 rounded-full flex-shrink-0" />}
                              <p className={'text-sm truncate '+(notif.is_read ? 'text-gray-700' : 'font-semibold text-gray-900')}>
                                {notif.title || notif.message}
                              </p>
                            </div>
                            {notif.message && notif.title && (
                              <p className="text-xs text-gray-500 line-clamp-2 mb-1">{notif.message}</p>
                            )}
                            <p className="text-[10px] text-gray-400">{timeAgo(notif.created_at)}</p>
                          </div>
                          <div className="flex items-center space-x-1 flex-shrink-0">
                            {!notif.is_read && (
                              <button onClick={function() { handleMarkRead([notif.id || notif.notification_id]); }}
                                className="p-1 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors" title="Mark read">
                                <Check className="h-3.5 w-3.5" />
                              </button>
                            )}
                            <button onClick={function() { handleDismissNotification(notif.id || notif.notification_id); }}
                              className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="Dismiss">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <EmptyState icon={BellOff} title="No notifications" description={notifFilter === 'unread' ? 'All caught up!' : 'Nothing here yet'} />
                )}
              </div>
            </section>

            {/* Quick Actions */}
            <section className="bg-white shadow border border-gray-100 text-left">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 text-left">Quick actions</h3>
              </div>
              <div className="p-3 space-y-1">
                <button onClick={function() { navigate('/dashboard/reports'); }}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors group">
                  <div className="flex items-center space-x-3">
                    <Search className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-700 group-hover:text-black">Search all reports</span>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-black" />
                </button>
                <button onClick={function() { navigate('/dashboard/reports?report_type=RTC'); }}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors group">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-700 group-hover:text-black">Rock tool cards</span>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-black" />
                </button>
                <button onClick={function() { navigate('/dashboard/reports?report_type=CSV'); }}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors group">
                  <div className="flex items-center space-x-3">
                    <BarChart3 className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-700 group-hover:text-black">Customer site visits</span>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-black" />
                </button>
                <button onClick={function() { navigate('/dashboard/reports?report_type=SA'); }}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors group">
                  <div className="flex items-center space-x-3">
                    <Hash className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-700 group-hover:text-black">Site audits</span>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-black" />
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* PDF Viewer Modal */}
      {pdfReportId && <PdfViewerModal reportId={pdfReportId} onClose={function() { setPdfReportId(null); }} />}
    </div>
  );
};

export default DashboardPage;
